/**
 * React Query Hooks for Jobs
 * Custom hooks for data fetching, mutations, and caching
 */

'use client';

import { useQuery, useMutation, useQueryClient, type UseQueryOptions, type UseMutationOptions } from '@tanstack/react-query';
import { jobsApiClient } from '../services/jobs-api';
import { QueryKeys, MutationKeys } from '@/types/api';
import type {
  Job,
  JobCreate,
  JobUpdate,
  JobListItem,
  Customer,
  CustomerCreate,
  CustomerUpdate,
  Vehicle,
  VehicleCreate,
  VehicleUpdate,
  Driver,
  DriverCreate,
  DriverUpdate,
  AccidentDetail,
  AccidentDetailCreate,
  AccidentDetailUpdate,
} from '@/types/job';
import type {
  Document,
  DocumentPage,
  DocumentUploadRequest,
  DocumentUploadResponse,
  DocumentListItem,
  FieldVerifyRequest,
  BulkFieldVerifyRequest,
  DocumentMapRequest,
  DocumentMapResponse,
} from '@/types/document';
import type { PaginatedResponse, JobsApiResponse, FilterOptions, SortOptions, ActivityLog } from '@/types/common';

// ============================================================================
// JOBS HOOKS
// ============================================================================

/**
 * Fetch jobs list with pagination and filters
 */
export function useJobs(
  params?: {
    page?: number;
    page_size?: number;
    filters?: FilterOptions;
    sort?: SortOptions;
  },
  options?: Omit<UseQueryOptions<JobsApiResponse<JobListItem>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.jobsList(params?.filters),
    queryFn: () => jobsApiClient.jobs.list(params),
    ...options,
  });
}

/**
 * Fetch single job by ID
 */
export function useJob(
  id: string,
  includeRelations = true,
  options?: Omit<UseQueryOptions<Job>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.job(id),
    queryFn: () => jobsApiClient.jobs.get(id, includeRelations),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch job activity log
 */
export function useJobActivity(
  id: string,
  params?: { page?: number; page_size?: number },
  options?: Omit<UseQueryOptions<PaginatedResponse<ActivityLog>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.jobActivity(id),
    queryFn: () => jobsApiClient.jobs.getActivity(id, params),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch job completion status
 */
export function useJobCompletion(
  id: string,
  options?: Omit<UseQueryOptions<unknown>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.jobCompletion(id),
    queryFn: () => jobsApiClient.jobs.getCompletion(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create new job
 */
export function useCreateJob(
  options?: UseMutationOptions<Job, Error, JobCreate>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.createJob,
    mutationFn: (data: JobCreate) => jobsApiClient.jobs.create(data),
    onSuccess: (newJob) => {
      // Invalidate jobs list to refetch
      queryClient.invalidateQueries({ queryKey: QueryKeys.jobs });
      // Optionally set the new job in cache
      queryClient.setQueryData(QueryKeys.job(newJob.id), newJob);
    },
    ...options,
  });
}

/**
 * Update existing job
 */
export function useUpdateJob(
  options?: UseMutationOptions<Job, Error, { id: string; data: JobUpdate }, { previousJob: Job | undefined }>
) {
  const queryClient = useQueryClient();

  return useMutation<Job, Error, { id: string; data: JobUpdate }, { previousJob: Job | undefined }>({
    mutationKey: MutationKeys.updateJob,
    mutationFn: ({ id, data }) => jobsApiClient.jobs.update(id, data),
    onMutate: async ({ id, data }): Promise<{ previousJob: Job | undefined }> => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: QueryKeys.job(id) });

      // Snapshot previous value
      const previousJob = queryClient.getQueryData<Job>(QueryKeys.job(id));

      // Optimistically update
      if (previousJob) {
        queryClient.setQueryData<Job>(QueryKeys.job(id), {
          ...previousJob,
          ...data,
        });
      }

      return { previousJob };
    },
    onError: (_err, { id }, context) => {
      // Rollback on error
      if (context?.previousJob) {
        queryClient.setQueryData(QueryKeys.job(id), context.previousJob);
      }
    },
    onSettled: (_data, _error, { id }) => {
      // Refetch after mutation
      queryClient.invalidateQueries({ queryKey: QueryKeys.job(id) });
      queryClient.invalidateQueries({ queryKey: QueryKeys.jobs });
    },
    ...options,
  });
}

/**
 * Delete job
 */
export function useDeleteJob(
  options?: UseMutationOptions<{ success: boolean }, Error, string>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.deleteJob,
    mutationFn: (id: string) => jobsApiClient.jobs.delete(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: QueryKeys.job(id) });
      // Invalidate jobs list
      queryClient.invalidateQueries({ queryKey: QueryKeys.jobs });
    },
    ...options,
  });
}

// ============================================================================
// CUSTOMERS HOOKS
// ============================================================================

/**
 * Fetch customer by ID
 */
export function useCustomer(
  id: string,
  options?: Omit<UseQueryOptions<Customer>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.customer(id),
    queryFn: () => jobsApiClient.customers.get(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Search customers
 */
export function useCustomerSearch(
  query: string,
  params?: { page?: number; page_size?: number },
  options?: Omit<UseQueryOptions<PaginatedResponse<Customer>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.customerSearch(query),
    queryFn: () => jobsApiClient.customers.search(query, params),
    enabled: query.length >= 2,
    ...options,
  });
}

/**
 * Create customer
 */
export function useCreateCustomer(
  options?: UseMutationOptions<Customer, Error, CustomerCreate>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.createCustomer,
    mutationFn: (data: CustomerCreate) => jobsApiClient.customers.create(data),
    onSuccess: (newCustomer) => {
      queryClient.setQueryData(QueryKeys.customer(newCustomer.id), newCustomer);
      queryClient.invalidateQueries({ queryKey: QueryKeys.customers });
    },
    ...options,
  });
}

/**
 * Update customer
 */
export function useUpdateCustomer(
  options?: UseMutationOptions<Customer, Error, { id: string; data: CustomerUpdate }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.updateCustomer,
    mutationFn: ({ id, data }) => jobsApiClient.customers.update(id, data),
    onSuccess: (updatedCustomer, { id }) => {
      queryClient.setQueryData(QueryKeys.customer(id), updatedCustomer);
      queryClient.invalidateQueries({ queryKey: QueryKeys.customers });
    },
    ...options,
  });
}

// ============================================================================
// VEHICLES HOOKS
// ============================================================================

/**
 * Fetch vehicle by ID
 */
export function useVehicle(
  id: string,
  options?: Omit<UseQueryOptions<Vehicle>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.vehicle(id),
    queryFn: () => jobsApiClient.vehicles.get(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Search vehicles
 */
export function useVehicleSearch(
  registration: string,
  options?: Omit<UseQueryOptions<Vehicle[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.vehicleSearch(registration),
    queryFn: () => jobsApiClient.vehicles.search(registration),
    enabled: registration.length >= 3,
    ...options,
  });
}

/**
 * Create vehicle
 */
export function useCreateVehicle(
  options?: UseMutationOptions<Vehicle, Error, VehicleCreate>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.createVehicle,
    mutationFn: (data: VehicleCreate) => jobsApiClient.vehicles.create(data),
    onSuccess: (newVehicle) => {
      queryClient.setQueryData(QueryKeys.vehicle(newVehicle.id), newVehicle);
      queryClient.invalidateQueries({ queryKey: QueryKeys.vehicles });
    },
    ...options,
  });
}

/**
 * Update vehicle
 */
export function useUpdateVehicle(
  options?: UseMutationOptions<Vehicle, Error, { id: string; data: VehicleUpdate }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.updateVehicle,
    mutationFn: ({ id, data }) => jobsApiClient.vehicles.update(id, data),
    onSuccess: (updatedVehicle, { id }) => {
      queryClient.setQueryData(QueryKeys.vehicle(id), updatedVehicle);
      queryClient.invalidateQueries({ queryKey: QueryKeys.vehicles });
    },
    ...options,
  });
}

// ============================================================================
// DRIVERS HOOKS
// ============================================================================

/**
 * Fetch driver by ID
 */
export function useDriver(
  id: string,
  options?: Omit<UseQueryOptions<Driver>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.driver(id),
    queryFn: () => jobsApiClient.drivers.get(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create driver
 */
export function useCreateDriver(
  options?: UseMutationOptions<Driver, Error, DriverCreate>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.createDriver,
    mutationFn: (data: DriverCreate) => jobsApiClient.drivers.create(data),
    onSuccess: (newDriver) => {
      queryClient.setQueryData(QueryKeys.driver(newDriver.id), newDriver);
      queryClient.invalidateQueries({ queryKey: QueryKeys.drivers });
    },
    ...options,
  });
}

/**
 * Update driver
 */
export function useUpdateDriver(
  options?: UseMutationOptions<Driver, Error, { id: string; data: DriverUpdate }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.updateDriver,
    mutationFn: ({ id, data }) => jobsApiClient.drivers.update(id, data),
    onSuccess: (updatedDriver, { id }) => {
      queryClient.setQueryData(QueryKeys.driver(id), updatedDriver);
      queryClient.invalidateQueries({ queryKey: QueryKeys.drivers });
    },
    ...options,
  });
}

// ============================================================================
// ACCIDENT DETAILS HOOKS
// ============================================================================

/**
 * Fetch accident detail by ID
 */
export function useAccidentDetail(
  id: string,
  options?: Omit<UseQueryOptions<AccidentDetail>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.accidentDetail(id),
    queryFn: () => jobsApiClient.accidentDetails.get(id),
    enabled: !!id,
    ...options,
  });
}

/**
 * Create accident detail
 */
export function useCreateAccidentDetail(
  options?: UseMutationOptions<AccidentDetail, Error, AccidentDetailCreate>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.createAccidentDetail,
    mutationFn: (data: AccidentDetailCreate) => jobsApiClient.accidentDetails.create(data),
    onSuccess: (newAccidentDetail) => {
      queryClient.setQueryData(QueryKeys.accidentDetail(newAccidentDetail.id), newAccidentDetail);
    },
    ...options,
  });
}

/**
 * Update accident detail
 */
export function useUpdateAccidentDetail(
  options?: UseMutationOptions<AccidentDetail, Error, { id: string; data: AccidentDetailUpdate }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.updateAccidentDetail,
    mutationFn: ({ id, data }) => jobsApiClient.accidentDetails.update(id, data),
    onSuccess: (updatedAccidentDetail, { id }) => {
      queryClient.setQueryData(QueryKeys.accidentDetail(id), updatedAccidentDetail);
    },
    ...options,
  });
}

// ============================================================================
// DOCUMENTS HOOKS
// ============================================================================

/**
 * Fetch documents for a job
 */
export function useJobDocuments(
  jobId: string,
  params?: {
    page?: number;
    page_size?: number;
    document_type?: string;
    document_category?: string;
    status?: string;
  },
  options?: Omit<UseQueryOptions<PaginatedResponse<DocumentListItem>>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.jobDocuments(jobId),
    queryFn: () => jobsApiClient.documents.list(jobId, params),
    enabled: !!jobId,
    ...options,
  });
}

/**
 * Fetch single document
 */
export function useDocument(
  id: string,
  includePages = true,
  options?: Omit<UseQueryOptions<Document>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.document(id),
    queryFn: () => jobsApiClient.documents.get(id, includePages),
    enabled: !!id,
    ...options,
  });
}

/**
 * Fetch document page
 */
export function useDocumentPage(
  documentId: string,
  pageNumber: number,
  options?: Omit<UseQueryOptions<DocumentPage>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: QueryKeys.documentPage(documentId, pageNumber),
    queryFn: () => jobsApiClient.documents.getPage(documentId, pageNumber),
    enabled: !!documentId && pageNumber > 0,
    ...options,
  });
}

/**
 * Get all pages for a document
 */
export function useDocumentPages(
  documentId: string,
  options?: Omit<UseQueryOptions<DocumentPage[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...QueryKeys.document(documentId), 'pages'],
    queryFn: async () => {
      // Fetch document to get page count
      const document = await jobsApiClient.documents.get(documentId, true);
      // Return pages array from document or empty array
      return document.pages || [];
    },
    enabled: !!documentId,
    ...options,
  });
}

/**
 * Upload document
 */
export function useUploadDocument(
  options?: UseMutationOptions<DocumentUploadResponse, Error, DocumentUploadRequest>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.uploadDocument,
    mutationFn: (data: DocumentUploadRequest) => jobsApiClient.documents.upload(data),
    onSuccess: (_response, variables) => {
      // Invalidate job documents list
      queryClient.invalidateQueries({ queryKey: QueryKeys.jobDocuments(variables.job_id) });
    },
    ...options,
  });
}

/**
 * Verify extracted field
 */
export function useVerifyField(
  options?: UseMutationOptions<DocumentPage, Error, { pageId: string; data: FieldVerifyRequest }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.verifyField,
    mutationFn: ({ pageId, data }) => jobsApiClient.documents.verifyField(pageId, data),
    onSuccess: (updatedPage) => {
      // Update page cache
      queryClient.setQueryData(
        QueryKeys.documentPage(updatedPage.document_id, updatedPage.page_number),
        updatedPage
      );
    },
    ...options,
  });
}

/**
 * Verify multiple fields in bulk
 */
export function useVerifyFieldsBulk(
  options?: UseMutationOptions<DocumentPage, Error, { pageId: string; data: BulkFieldVerifyRequest }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.verifyFieldsBulk,
    mutationFn: ({ pageId, data }) => jobsApiClient.documents.verifyFieldsBulk(pageId, data),
    onSuccess: (updatedPage) => {
      queryClient.setQueryData(
        QueryKeys.documentPage(updatedPage.document_id, updatedPage.page_number),
        updatedPage
      );
    },
    ...options,
  });
}

/**
 * Map document to entity
 */
export function useMapDocument(
  options?: UseMutationOptions<DocumentMapResponse, Error, { documentId: string; data: DocumentMapRequest }>
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: MutationKeys.mapDocument,
    mutationFn: ({ documentId, data }) => jobsApiClient.documents.mapToEntity(documentId, data),
    onSuccess: (response, { documentId }) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: QueryKeys.document(documentId) });

      // Invalidate the mapped entity
      const entityType = response.mapping.entity_type;
      const entityId = response.mapping.entity_id;

      if (entityType === 'customer') {
        queryClient.invalidateQueries({ queryKey: QueryKeys.customer(entityId) });
      } else if (entityType === 'vehicle') {
        queryClient.invalidateQueries({ queryKey: QueryKeys.vehicle(entityId) });
      } else if (entityType === 'driver') {
        queryClient.invalidateQueries({ queryKey: QueryKeys.driver(entityId) });
      } else if (entityType === 'accident_detail') {
        queryClient.invalidateQueries({ queryKey: QueryKeys.accidentDetail(entityId) });
      }
    },
    ...options,
  });
}
